"use client";

import { useState, useRef, useEffect } from "react";

interface MediaWithFallbackProps {
  src: string;
  alt: string;
  type?: "image" | "video";
  className?: string;
  loading?: "lazy" | "eager";
  role?: string;
  tabIndex?: number;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  // Video-specific props
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  autoPlay?: boolean;
  playbackRate?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function MediaWithFallback({
  src,
  alt,
  type = "image",
  className = "",
  loading = "lazy",
  role,
  tabIndex,
  onClick,
  onKeyDown,
  loop = true,
  muted = true,
  playsInline = true,
  autoPlay,
  playbackRate,
  onMouseEnter,
  onMouseLeave,
}: MediaWithFallbackProps) {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Handle playback rate for videos
  useEffect(() => {
    if (videoRef.current && playbackRate) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const handleError = () => {
    setError(true);
    setIsLoading(false);
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  if (error) {
    return (
      <div
        className={`flex items-center justify-center bg-[rgba(var(--foreground-rgb),0.05)] border border-dashed border-[rgba(var(--foreground-rgb),0.15)] ${className}`}
        style={{ aspectRatio: "16/9" }}
        role="img"
        aria-label={`${alt} (failed to load)`}
      >
        <div className="flex flex-col items-center gap-2 text-[rgba(var(--foreground-rgb),0.3)]">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {type === "video" ? (
              <>
                <rect x="2" y="2" width="20" height="20" rx="2" />
                <path d="M10 8l6 4-6 4V8z" />
                <line x1="4" y1="4" x2="20" y2="20" />
              </>
            ) : (
              <>
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <line x1="4" y1="4" x2="20" y2="20" />
              </>
            )}
          </svg>
          <span className="text-xs font-mono">Media unavailable</span>
        </div>
      </div>
    );
  }

  if (type === "video") {
    return (
      <video
        ref={videoRef}
        src={src}
        preload="metadata"
        className={className}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        autoPlay={autoPlay}
        role={role}
        tabIndex={tabIndex}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onError={handleError}
        onLoadedData={handleLoad}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      role={role}
      tabIndex={tabIndex}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
}
